import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BRANCHES, BRANDS, REGIONS } from "../lib/data";
import { BRANCH_PRODUCTIVITY, SSSG_TREND, NETWORK_FUNNEL, PEER_BENCHMARK } from "../lib/dataExtras";
import { Tile, Badge, SectionHeader } from "../components/Primitives";
import PhilippinesMap from "../components/PhilippinesMap";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import { useRole } from "../lib/role.jsx";
import { Search, ArrowUpDown, Sparkles, Users, Store } from "lucide-react";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, ReferenceLine, BarChart, Bar, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ZAxis,
} from "recharts";
import { useChartColors } from "../lib/chartColors";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg max-w-xs">
      {label && <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value, p.name) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function BranchPerformance() {
  const navigate = useNavigate();
  const C = useChartColors();
  const { branchScope, role } = useRole();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("achievement");
  const [sortDir, setSortDir] = useState("desc");

  // Branch-scoped roles only see their own branch.
  const scopedBranches = useMemo(() => (
    branchScope ? BRANCHES.filter(b => b.id === branchScope.branchId) : BRANCHES
  ), [branchScope]);

  const filtered = useMemo(() => {
    let list = scopedBranches;
    if (filter !== "all") list = list.filter(b => b.brand === filter || b.region === filter);
    if (query) list = list.filter(b => b.name.toLowerCase().includes(query.toLowerCase()));
    list = [...list].sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      return (a[sortBy] > b[sortBy] ? 1 : -1) * dir;
    });
    return list;
  }, [filter, query, sortBy, sortDir]);

  const aggregates = useMemo(() => ({
    revenue: filtered.reduce((s, b) => s + b.monthlyActual, 0),
    target: filtered.reduce((s, b) => s + b.monthlyTarget, 0),
    avgAchievement: filtered.reduce((s, b) => s + b.achievement, 0) / Math.max(filtered.length, 1),
    avgStockout: filtered.reduce((s, b) => s + b.stockoutRate, 0) / Math.max(filtered.length, 1),
    count: filtered.length,
  }), [filtered]);

  // Quadrant data — Revenue (₱M) × Achievement (%)
  const quadrant = useMemo(() => BRANCH_PRODUCTIVITY.map(p => {
    const b = BRANCHES.find(b => b.id === p.branchId);
    return {
      id: b.id,
      name: b.name,
      x: b.monthlyActual / 1e6,
      y: b.achievement * 100,
      z: p.healthScore,
      region: b.region,
      brand: b.brand,
      quadrant: b.achievement >= 1 && b.monthlyActual >= 4_500_000 ? "Star"
              : b.achievement >= 1 ? "Investing"
              : b.monthlyActual >= 4_500_000 ? "Stable"
              : "At-Risk",
    };
  }), []);

  const qColor = { Star: C.success, Investing: C.info, Stable: C.primary, "At-Risk": C.critical };

  // Top / bottom by health score
  const ranked = useMemo(() => [...BRANCH_PRODUCTIVITY].sort((a, b) => b.healthScore - a.healthScore), []);

  function setSort(col) {
    if (sortBy === col) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART I + VII · Branch Network" title="Branch Performance"
        description="Real-time achievement, productivity, health score & ranking across 86+ branches in three brands." />

      {branchScope && (
        <div data-testid="branch-scope-banner" className="tile p-3 flex items-center gap-2.5 text-[12px] border-l-2 border-primary/60 bg-primary/5">
          <Badge status="info">Branch-scoped</Badge>
          <span className="text-fg/90">Showing only <span className="font-medium">{branchScope.branchName}</span> — your assigned branch as {role?.name}. Enterprise views are restricted.</span>
        </div>
      )}

      {/* Filters */}
      <div className="tile p-3 flex flex-wrap items-center gap-2" data-testid="branch-filters">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" />
        {BRANDS.map(b => <FilterChip key={b} active={filter === b} onClick={() => setFilter(b)} label={b} />)}
        <div className="w-px h-5 bg-border mx-1" />
        {REGIONS.map(r => <FilterChip key={r} active={filter === r} onClick={() => setFilter(r)} label={r} />)}
        <div className="flex-1" />
        <div className="flex items-center gap-2 px-3 h-9 bg-surface-2 border border-border rounded-sm w-64">
          <Search size={14} className="text-muted" />
          <input data-testid="branch-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search branch…" className="bg-transparent outline-none text-sm w-full placeholder:text-subtle" />
        </div>
      </div>

      {/* Aggregate strip */}
      <div className="tile grid grid-cols-2 md:grid-cols-6 divide-x divide-border" data-testid="branch-aggregate-strip">
        <Agg label="Branches" value={aggregates.count} />
        <Agg label="Revenue MTD" value={formatPeso(aggregates.revenue, { compact: true })} />
        <Agg label="Target MTD" value={formatPeso(aggregates.target, { compact: true })} />
        <Agg label="Avg Achievement" value={`${(aggregates.avgAchievement * 100).toFixed(0)}%`} status={aggregates.avgAchievement >= 1 ? "success" : "warning"} />
        <Agg label="Avg Stockout" value={`${(aggregates.avgStockout * 100).toFixed(1)}%`} status={aggregates.avgStockout > 0.06 ? "warning" : "success"} />
        <Agg label="Network Health" value={Math.round(BRANCH_PRODUCTIVITY.reduce((s, p) => s + p.healthScore, 0) / BRANCH_PRODUCTIVITY.length)} status="success" />
      </div>

      {/* Row 1 — quadrant + SSSG */}
      <div className="grid grid-cols-12 gap-6">
        <Tile className="col-span-12 lg:col-span-8" testid="branch-quadrant"
              title="Performance Quadrant — Revenue × Achievement"
              action={<div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
                {Object.keys(qColor).map(k => (
                  <span key={k} className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: qColor[k] }} />{k}</span>
                ))}
              </div>}>
          <div style={{ height: 320 }}>
            <ResponsiveContainer>
              <ScatterChart margin={{ top: 10, right: 16, left: 4, bottom: 6 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
                <XAxis type="number" dataKey="x" name="Revenue" tick={tickStyle} stroke={C.borderStrong}
                       tickFormatter={v => `₱${v.toFixed(0)}M`} label={{ value: "Revenue MTD (₱M)", position: "insideBottom", offset: -6, style: { fontSize: 10, fill: "rgb(var(--muted))" } }} />
                <YAxis type="number" dataKey="y" name="Achievement %" tick={tickStyle} stroke={C.borderStrong}
                       domain={[60, 130]} tickFormatter={v => `${v}%`} />
                <ZAxis type="number" dataKey="z" range={[60, 280]} />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
                      <div className="font-semibold text-fg mb-1">{p.name}</div>
                      <div className="text-muted">{p.region} · {p.brand}</div>
                      <div className="font-mono-tabular text-fg mt-1">₱{p.x.toFixed(1)}M · {p.y.toFixed(0)}% achievement</div>
                      <div className="font-mono-tabular text-primary">Health: {p.z}</div>
                      <div className="mt-1"><Badge status={p.quadrant === "Star" ? "success" : p.quadrant === "At-Risk" ? "critical" : "info"}>{p.quadrant}</Badge></div>
                    </div>
                  );
                }} />
                <ReferenceLine x={4.5} stroke={C.borderStrong} strokeDasharray="3 3" />
                <ReferenceLine y={100} stroke={C.borderStrong} strokeDasharray="3 3" />
                {Object.keys(qColor).map(q => (
                  <Scatter key={q} name={q} data={quadrant.filter(p => p.quadrant === q)} fill={qColor[q]}
                           onClick={(d) => navigate(`/branches/${d.id}`)} cursor="pointer" />
                ))}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 text-[11px] text-muted">
            <strong className="text-fg">Reading:</strong> Top-right = Stars (scale + grow). Bottom-left = At-Risk (rescue or rationalize). Bubble size = AI Health Score. Click any branch to drill-down.
          </div>
        </Tile>

        <Tile className="col-span-12 lg:col-span-4" testid="sssg-trend"
              title="Same-Store Sales Growth — last 12 weeks">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={SSSG_TREND} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="week" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `${(v * 100).toFixed(0)}%`} />
                <ReferenceLine y={0} stroke={C.borderStrong} strokeDasharray="3 3" />
                <Tooltip content={<TT formatter={v => `${(v * 100).toFixed(1)}%`} />} />
                {REGIONS.map((r, i) => (
                  <Line key={r} type="monotone" dataKey={r} stroke={C.chart[i % 5]} strokeWidth={1.8} dot={false} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1 mt-1">
            {REGIONS.map((r, i) => {
              const lastVal = SSSG_TREND[SSSG_TREND.length - 1][r];
              return (
                <div key={r} className="flex items-center justify-between text-[11px]">
                  <span className="inline-flex items-center gap-2"><span className="w-2 h-2" style={{ background: C.chart[i % 5] }} />{r}</span>
                  <span className={cn("font-mono-tabular", lastVal >= 0 ? "text-success" : "text-critical")}>{lastVal >= 0 ? "+" : ""}{(lastVal * 100).toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </Tile>

        {/* Row 2 — productivity + funnel + radar */}
        <Tile className="col-span-12 lg:col-span-5" testid="productivity-top"
              title="Productivity Leaders — Revenue per sq.m" action={<Badge status="success">Top 10</Badge>}>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={[...BRANCH_PRODUCTIVITY].sort((a, b) => b.revPerSqm - a.revPerSqm).slice(0, 10)}
                        layout="vertical" margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1000).toFixed(0)}K`} />
                <YAxis dataKey="name" type="category" tick={{ ...tickStyle, fontFamily: "IBM Plex Sans" }} stroke={C.borderStrong} width={150} />
                <Tooltip content={<TT formatter={v => formatPeso(v)} />} cursor={{ fill: C.surface2 }} />
                <Bar dataKey="revPerSqm" fill={C.chart[0]} radius={[0, 2, 2, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        <Tile className="col-span-12 lg:col-span-4" testid="network-funnel" title="Network Funnel — MTD">
          <div className="space-y-3 mt-1">
            {NETWORK_FUNNEL.map((stage, i) => {
              const pctOfTop = stage.value / NETWORK_FUNNEL[0].value;
              return (
                <div key={stage.stage}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{stage.stage}</span>
                    <span className="font-mono-tabular">{formatNumber(stage.value, { compact: true })}</span>
                  </div>
                  <div className="h-7 bg-surface-2 rounded-sm overflow-hidden flex items-center relative">
                    <div className="h-full flex items-center justify-end pr-2 text-[10px] font-mono-tabular text-bg" style={{
                      width: `${pctOfTop * 100}%`,
                      background: `rgb(var(--chart-${(i % 5) + 1}))`,
                    }}>
                      {(pctOfTop * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="text-[10px] text-muted mt-1">{stage.label}</div>
                </div>
              );
            })}
          </div>
        </Tile>

        <Tile className="col-span-12 lg:col-span-3" testid="peer-radar" title="Peer Benchmark Radar">
          <div style={{ height: 220 }}>
            <ResponsiveContainer>
              <RadarChart data={PEER_BENCHMARK.radar} outerRadius={70}>
                <PolarGrid stroke={C.border} />
                <PolarAngleAxis dataKey="axis" tick={{ fill: "rgb(var(--muted))", fontSize: 10 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} stroke={C.border} />
                <Radar name="Top" dataKey="Top" stroke={C.success} fill={C.success} fillOpacity={0.25} />
                <Radar name="Average" dataKey="Average" stroke={C.primary} fill={C.primary} fillOpacity={0.18} />
                <Radar name="At-Risk" dataKey="AtRisk" stroke={C.critical} fill={C.critical} fillOpacity={0.20} />
                <Tooltip content={<TT />} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-muted mt-1 leading-relaxed">
            <span className="text-success">●</span> {PEER_BENCHMARK.top.name} ·
            <span className="text-critical ml-2">●</span> {PEER_BENCHMARK.atRisk.name}
          </div>
        </Tile>

        {/* Row 3 — AI Branch Health Score ranking */}
        <Tile className="col-span-12 lg:col-span-6" testid="health-top" title="AI Branch Health Score — Top 8" action={<Badge status="success">Highest composite</Badge>}>
          <HealthList items={ranked.slice(0, 8)} good />
        </Tile>
        <Tile className="col-span-12 lg:col-span-6" testid="health-bottom" title="AI Branch Health Score — Bottom 8" action={<Badge status="critical">Needs attention</Badge>}>
          <HealthList items={ranked.slice(-8).reverse()} good={false} />
        </Tile>

        {/* Map + Table (original) */}
        <Tile className="col-span-12 lg:col-span-5" title="Geographic Distribution">
          <div className="h-[520px]"><PhilippinesMap filter={filter} /></div>
        </Tile>
        <Tile className="col-span-12 lg:col-span-7" title={`Branch Detail (${filtered.length})`} dense>
          <div className="overflow-auto max-h-[520px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface z-10">
                <tr className="text-[10px] uppercase tracking-widest text-muted">
                  <Th label="Branch" col="name" sortBy={sortBy} sortDir={sortDir} onClick={setSort} />
                  <Th label="Brand" col="brand" sortBy={sortBy} sortDir={sortDir} onClick={setSort} />
                  <Th label="Rev MTD" col="monthlyActual" sortBy={sortBy} sortDir={sortDir} onClick={setSort} align="right" />
                  <Th label="Ach" col="achievement" sortBy={sortBy} sortDir={sortDir} onClick={setSort} align="right" />
                  <Th label="Stockout" col="stockoutRate" sortBy={sortBy} sortDir={sortDir} onClick={setSort} align="right" />
                  <Th label="DOH" col="daysOnHand" sortBy={sortBy} sortDir={sortDir} onClick={setSort} align="right" />
                  <Th label="Inv ₱" col="inventoryValue" sortBy={sortBy} sortDir={sortDir} onClick={setSort} align="right" />
                </tr>
              </thead>
              <tbody className="font-mono-tabular">
                {filtered.map(b => (
                  <tr key={b.id} data-testid={`branch-row-${b.id}`}
                      onClick={() => navigate(`/branches/${b.id}`)}
                      className="border-t border-border hover:bg-surface-2 transition-colors cursor-pointer">
                    <td className="py-2 px-3 font-sans">
                      <div className="font-medium">{b.name}</div>
                      <div className="text-[10px] text-muted">{b.region}</div>
                    </td>
                    <td className="py-2 px-3"><Badge status="neutral">{b.brand}</Badge></td>
                    <td className="py-2 px-3 text-right">{formatPeso(b.monthlyActual, { compact: true })}</td>
                    <td className={cn("py-2 px-3 text-right font-semibold", b.achievement >= 1 ? "text-success" : b.achievement >= 0.9 ? "text-warning" : "text-critical")}>
                      {(b.achievement * 100).toFixed(0)}%
                    </td>
                    <td className={cn("py-2 px-3 text-right", b.stockoutRate > 0.06 ? "text-critical" : "text-fg")}>
                      {(b.stockoutRate * 100).toFixed(1)}%
                    </td>
                    <td className={cn("py-2 px-3 text-right", b.daysOnHand > 60 ? "text-warning" : "text-fg")}>{b.daysOnHand}</td>
                    <td className="py-2 px-3 text-right">{formatPeso(b.inventoryValue, { compact: true })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tile>
      </div>
    </div>
  );
}

function HealthList({ items, good }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-2.5">
      {items.map((p, i) => {
        const ach = BRANCHES.find(b => b.id === p.branchId)?.achievement || 0;
        return (
          <div key={p.branchId} onClick={() => navigate(`/branches/${p.branchId}`)}
               data-testid={`health-row-${p.branchId}`}
               className="flex items-center gap-3 px-3 py-2 hover:bg-surface-2 cursor-pointer rounded-sm border border-border bg-surface-2/40">
            <div className="text-[10px] font-mono-tabular text-muted w-5">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{p.name}</div>
              <div className="text-[10px] text-muted">{p.region} · {p.brand}</div>
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { k: "rev", lbl: "Rev" }, { k: "stock", lbl: "Stk" }, { k: "prod", lbl: "Pdt" }, { k: "acc", lbl: "Acc" }, { k: "nps", lbl: "NPS" },
              ].map(c => {
                const v = p.components[c.k];
                const bar = Math.max(2, Math.round(v / 5));
                return (
                  <div key={c.k} title={`${c.lbl}: ${v}`} className="w-1.5 rounded-sm" style={{
                    height: bar, background: v >= 70 ? "rgb(var(--success))" : v >= 50 ? "rgb(var(--warning))" : "rgb(var(--critical))",
                  }} />
                );
              })}
            </div>
            <div className={cn("text-right font-mono-tabular font-semibold w-12",
              p.healthScore >= 75 ? "text-success" : p.healthScore >= 55 ? "text-warning" : "text-critical")}>
              {p.healthScore}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      data-testid={`filter-${label.replace(/ /g, "-").toLowerCase()}`}
      className={cn("text-xs px-3 h-8 border rounded-sm transition-colors",
        active ? "bg-primary text-primary-fg border-primary" : "bg-surface-2 border-border hover:bg-surface")}>
      {label}
    </button>
  );
}

function Agg({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-2">{status && <span className={`w-2 h-2 rounded-full ${status === "success" ? "bg-success" : status === "warning" ? "bg-warning" : "bg-critical"}`} />}{label}</div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}

function Th({ label, col, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === col;
  return (
    <th onClick={() => onClick(col)} className={cn("py-2 px-3 cursor-pointer select-none border-b border-border", align === "right" ? "text-right" : "text-left")}>
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={10} className={cn(active ? "text-primary" : "text-subtle")} />
      </span>
    </th>
  );
}
